import { useEffect, useRef, useState } from 'react'
import { Icon } from '../../../components/icons/Icon'
import { StatusPill } from '../../../components/StatusPill'
import { FocusTrapDialog } from '../../../components/FocusTrapDialog'
import { InlineAlert } from '../../../components/InlineAlert'
import { lifecycleStatusPill } from './lifecycleStatus'
import { createRuleSetDraft, listRuleSets } from '../../../api/cartridgeStudio'
import { ApiError } from '../../../api/problem'
import type { RuleSetView } from '../../../types/cartridgeStudio'
import './RuleSetPicker.css'

export interface RuleSetPickerProps {
  programId: string
  selectedId: string | null
  onSelect: (ruleSet: RuleSetView) => void
  /** Bumped by the caller to force a refetch after a lifecycle transition changes a row's status. */
  refreshToken?: number
}

/**
 * A compact per-program list — NOT a full `ListView` (design doc §4.1: `CursorPage<RuleSetView>`
 * for one program is small; a full universal-control-set list would be over-built for a handful
 * of rows). `GET .../rule-sets`, `POST .../rule-sets` (draft-creation only, per
 * `RuleSetController`'s javadoc — never a publish shortcut).
 */
export function RuleSetPicker({ programId, selectedId, onSelect, refreshToken }: RuleSetPickerProps) {
  const [ruleSets, setRuleSets] = useState<RuleSetView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    // See ProgramWorkspaceScreen's effect for why the `controller.signal.aborted` guard is needed
    // (React 18 StrictMode double-invoke race).
    const controller = new AbortController()
    setError(null)
    listRuleSets(programId, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return
        setRuleSets(page.items)
        if (!selectedId && page.items.length > 0) onSelect(page.items[0]!)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if (err instanceof ApiError) setError(err.problem.detail ?? err.message)
        else setError('Could not load RuleSets for this program.')
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, refreshToken])

  return (
    <div className="iw-rspicker">
      <div className="iw-rspicker__header">
        <span className="iw-rspicker__title">RuleSets</span>
        <button type="button" className="iw-btn iw-btn--sm" onClick={() => setDialogOpen(true)}>
          <Icon name="plus" size={13} />
          New draft
        </button>
      </div>

      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

      {ruleSets === null && !error ? (
        <div className="iw-rspicker__empty">Loading…</div>
      ) : ruleSets && ruleSets.length === 0 ? (
        <div className="iw-rspicker__empty">No RuleSets yet for this program — create the first draft.</div>
      ) : (
        <ul className="iw-rspicker__list" role="list">
          {ruleSets?.map((rs) => (
            <li key={rs.id}>
              <button
                type="button"
                className={rs.id === selectedId ? 'iw-rspicker__row iw-rspicker__row--selected' : 'iw-rspicker__row'}
                onClick={() => onSelect(rs)}
              >
                <span className="iw-rspicker__version">v{rs.structureVersion}</span>
                <span className="iw-rspicker__dates">
                  {rs.validFrom} → {rs.validTo ?? 'open'}
                </span>
                <StatusPill {...lifecycleStatusPill(rs.status)} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {dialogOpen ? (
        <NewRuleSetDialog
          programId={programId}
          onClose={() => setDialogOpen(false)}
          onCreated={(created) => {
            setDialogOpen(false)
            setRuleSets((prev) => [created, ...(prev ?? [])])
            onSelect(created)
          }}
        />
      ) : null}
    </div>
  )
}

function NewRuleSetDialog({
  programId,
  onClose,
  onCreated,
}: {
  programId: string
  onClose: () => void
  onCreated: (ruleSet: RuleSetView) => void
}) {
  const [structureVersion, setStructureVersion] = useState('1')
  const [validFrom, setValidFrom] = useState(() => new Date().toISOString().slice(0, 10))
  const [validTo, setValidTo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const versionRef = useRef<HTMLInputElement>(null)

  const parsedVersion = Number(structureVersion)
  const isValid = Number.isInteger(parsedVersion) && parsedVersion >= 1 && validFrom !== ''

  async function handleSubmit() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const created = await createRuleSetDraft(programId, {
        structureVersion: parsedVersion,
        validFrom,
        validTo: validTo || null,
      })
      onCreated(created)
    } catch (err) {
      setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not create this RuleSet draft.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="new-ruleset-title"
      title="New RuleSet draft"
      onClose={onClose}
      initialFocusRef={versionRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleSubmit()} disabled={!isValid || submitting}>
            {submitting ? 'Creating…' : 'Create draft'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-ruleset-version">
            Structure version
          </label>
          <input
            id="new-ruleset-version"
            ref={versionRef}
            className="iw-input"
            type="number"
            min={1}
            value={structureVersion}
            onChange={(e) => setStructureVersion(e.target.value)}
          />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-ruleset-valid-from">
            Valid from
          </label>
          <input
            id="new-ruleset-valid-from"
            className="iw-input"
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
          />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-ruleset-valid-to">
            Valid to (optional — leaves the window open)
          </label>
          <input id="new-ruleset-valid-to" className="iw-input" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
        </div>
      </form>
    </FocusTrapDialog>
  )
}
